/**
 * Attribute templates collection
 * This collection contains ALL attributes
 * It creates two sub collections of asset and clip attributes and maintains the two
 * No need to add to them individually - just add to this collection and they will get to where they need to be
 * @class
 */
import {ATTRIBUTE_SYSTEM_ID} from '@netx/core-attribute/constants';
import {correctAttributeTemplates} from '@netx/core-attribute/utils/correctAttributeTemplates';
import {isBrandPortal} from '@netx/core-cms-next/utils/isBrandPortal';
import {ENFORCE_ATTRIBUTE_IDS} from '@netx/core-cms/services/settings/constants';
import {getPreference} from '@netx/core-user-next/utils/getPreference';
import Base from '@netx/core/lib/netx-collection';
import {isEmptyValue} from '@netx/core/lib/underscore-netx';
import BaseModel from '../models/attribute';
import DateModel from '../models/attribute_date';
import NumberModel from '../models/attribute_number';
import SelectModel from '../models/attribute_select';
import SemanticModel from '../models/attribute_semantic';
import TagModel from '../models/attribute_tag';
import TextModel from '../models/attribute_text';
import TextareaModel from '../models/attribute_textarea';
import {ATTRIBUTE_VOCABULARY_TYPES} from '@netx/core-attribute-vocabulary/constants';
import {
	AttributeType,
	ATTRIBUTE_TYPE_NAME,
} from '@netx/core-attribute/constants';
import AssetAttributeDatumCollection from './assetAttributeDatum';

const Models = {
	base: BaseModel,
	date: DateModel,
	number: NumberModel,
	select: SelectModel,
	semantic: SemanticModel,
	tag: TagModel,
	text: TextModel,
	textarea: TextareaModel,
};

const getPortal = () =>
	isBrandPortal() && Netx.cms?.portal?.id && Netx.cms?.portal;

export default class Attributes extends Base {
	/**
	 * @type {Netx.Rpc}
	 * @private
	 */
	get rpc() {
		return true;
	}

	/** @type {boolean} */
	get xhrTrackProgress() {
		return true;
	}

	/** @type {object} */
	get xhrLoadingMessages() {
		return {
			getAttributeTemplates: 'Loading attributes',
		};
	}

	/** @type {object} */
	get xhrLoadedMessages() {
		return {
			getAttributeTemplates: 'Loaded attributes',
		};
	}

	/**
	 * Since we are overriding the model proeprty as a method
	 * we need to point backbone into the right direction with model prototype accessing
	 * This is the only method in all of the backbone collection code that relys on getting the a property from the model prototype
	 * @override
	 * @param {object} attrs Attributes
	 * @return {mixed} Model id
	 */
	modelId(attrs) {
		return attrs[Models.base.prototype.idAttribute || 'id'];
	}

	/**
	 * Return an array of the IDs of the models in this collection.
	 * Se note about `this.modelId` - this is an override of code in module:core/lib/netx-collection
	 * @override
	 * @param {boolean} omitSystem Omit system attributes
	 * @return {array} Array of model ids
	 */
	ids(omitSystem) {
		const ids = this.pluck(Models.base.prototype.idAttribute || 'id');
		if (!omitSystem) return ids;

		// Omit system attributes
		return ids.filter((id) => id > 0);
	}

	/** @method */
	comparator(attrA, attrB) {
		// Portal attributes
		// DAM-20153: if there is a portal, sort by attributeIds array
		const portal = getPortal();
		if (portal) {
			const attributeIds = portal
				.get('attributes')
				.map((attr) => attr.attributeTemplateId);

			const indexA = attributeIds.indexOf(attrA.id);
			const indexB = attributeIds.indexOf(attrB.id);

			// if the index was not found then we have a mismatch between the porta `attributes` and the
			// portal `attributeIds` and something did not go right in the migration
			//
			// if the data has a mismatch it is most likely due to the fact that the attribute was not selected
			// as a detail or sort attribute
			// in this event they most likely do not want it to be their primary so we will push it to the bottom
			// this could be the case for required attributes (like `file` and `name`)
			if (indexA === -1) return 1;
			// same as the above, if the attribute we are comparing against does not have a valid index
			// we want to make sure it is at the bottom of the list
			if (indexB === -1) return -1;
			return indexA - indexB;
		}

		// App attributes

		// DAM-11580
		// If attrA is `file` it comes before
		if (attrA.id === ATTRIBUTE_SYSTEM_ID.file) {
			return -1;
		}
		// If attrB is `file`, attrA comes after
		else if (attrB.id === ATTRIBUTE_SYSTEM_ID.file) {
			return 1;
		}

		if (this.keepAlphabetical) {
			const aName = attrA.get('name').toLowerCase();
			const bName = attrB.get('name').toLowerCase();
			// This is then the standard sort you would expect by `name`
			if (aName < bName) {
				return -1;
			}
			if (bName < aName) {
				return 1;
			}
		} else {
			const aOrderby = attrA.get('orderby');
			const bOrderby = attrB.get('orderby');
			// This is then the standard sort you would expect by `orderby`
			if (aOrderby < bOrderby) {
				return -1;
			}
			if (bOrderby < aOrderby) {
				return 1;
			}
		}

		// Equal
		return 0;
	}

	/**
	 * @property {object} methods                                          - Collection RPC methods
	 * @property {method} methods.read                                     - Fetch attribute templates
	 *
	 * @property {method} methods.getAttributeTemplates                    - Fetch attribute templates
	 * @property {method} methods.getAttributeObjects                      - Fetch attribute templates by id
	 * @property {method} methods.getAllAttributeIds                       - Fetch attribute templates ids
	 * @property {method} methods.getDataSetList                           - Fetch datasets (deprecated)
	 * @property {method} methods.setAttributeTemplateOrder                - Set new attribute template order
	 */
	get methods() {
		return {
			/**
			 * @function
			 * @param {object} opts Request options
			 */
			read(opts) {
				// First get the templates
				return this.getAttributeTemplates(opts).then((attrs) => {
					this.set(attrs);
					return attrs;
				});
			},

			/** @method */
			getAllAttributeIds: ['getAllAttributeIds'],
			/** @method */
			getAttributeObjects: ['getAttributeObjects', 'attributeIds'],
			/** @method */
			getAttributeTemplates: ['getAttributeTemplates'],
			/**
			 * @method
			 * @param {object} opts Request options
			 * @deprecated
			 * @see parsers.getDataSetList
			 */
			getDataSetList(opts) {
				console.warn(
					'Do not fetch datasets via the attributes collection. Method will be removed in 9.0 and will result in an error.',
				);
				return ['getDataSetList', ''];
			},
			/**
			 * @method
			 * @param {array} ids IDs
			 * @param {object} opts Request options
			 */
			setAttributeTemplateOrder(ids, opts) {
				return ['setAttributeTemplateOrder', ids, false];
			},
		};
	}

	/**
	 * @property {object}   parsers                                          - Collection parsers
	 * @property {function} parsers.getAllAttributeIds                       - Parse fetch of attribute ids
	 * @property {function} parsers.getAttributeObjects                      - Parse fetch of attribute templates
	 * @property {function} parsers.getAttributeTemplates                    - Parse fetch of attribute templates
	 * @property {function} parsers.getDataSetList                           - Parse fetch of datasets (deprecated)
	 * @property {function} parsers.setAttributeTemplateOrder                - Parse update of attribute template order
	 */
	get parsers() {
		return {
			/**
			 * @function
			 * @param {array} ids Attribute ids
			 * @param {object} opts Request options
			 */
			getAllAttributeIds(ids, opts) {
				// NOTE: the attribute object's returned id field is 'attributeid', all lower case,
				// while the RPC call is getAllAttributeIds() (uppercase I).
				// I'm keeping with the somewhat standard "Ids" format used in the rest of the codebase.
				this.attributeIds = ids;
			},
			/**
			 * @function
			 * @param {array} attributes Attribute templates
			 * @param {object} opts Request options
			 */
			getAttributeObjects(attributes, opts) {
				this.set(correctAttributeTemplates(attributes), opts);
			},
			/**
			 * @function
			 * @param {array} attributes Attribute templates
			 * @param {object} opts Request options
			 */
			getAttributeTemplates(attributes, opts) {
				return correctAttributeTemplates(attributes);
			},
			/**
			 * @function
			 * @param {array} datasets Datasets
			 * @param {object} opts Request options
			 * @deprecated
			 */
			getDataSetList(datasets, opts) {
				// TODO: No! thise objects are nothin like our models whatsoever
				// it does not look like anything is using this
				// Deprecating
				console.warn('How do you like them errors in the console?');
				this.set(datasets, opts);
			},
			/**
			 * @function
			 * @param {null} response Null
			 * @param {array} ids IDs
			 * @param {object} opts Request options
			 */
			setAttributeTemplateOrder(response, ids, opts) {
				// Correct our models `orderby`
				let model;
				_.each(ids, (id, idx) => {
					model = this.get(id);
					// Set silently as we will sort after this
					// If we trigger a change on each one we can cause the ui to become sluggish
					// as it will trigger renders and all sorts of unneeded checks
					model && model.set('orderby', idx + 1, {silent: true});
				});
				this.sort();
			},
		};
	}

	/** @override */
	constructor(models, opts = {}) {
		opts.model =
			opts.model ||
			function (attrs, opts) {
				const typeName = ATTRIBUTE_TYPE_NAME[attrs.type];
				const modelClass = Models[typeName] || Models.base;
				return new modelClass(attrs, opts);
			};

		// Circular
		//AssetAttributeDatumCollection = require('./assetAttributeDatum');
		super(models, opts);

		/**
		 * For use with `getAttributeTemplatesForCategoryContext`
		 * Only respected when `permission.attributesEnabled` (deprecated) is true
		 * @memberOf Netx.Collections.Attributes
		 * @todo Andrew has noted that we will be eventually only allowing a single id here
		 * We do not use more than one id as of yet and should not moving forward. The request
		 * method will just send the first id and console log a warning if there is more than
		 * one present in the mean time.
		 * @alias categoryIds
		 * @type {number}
		 * @deprecated
		 */
		this.categoryIds = [];

		/**
		 * @memberof module:asset/collections/attributes
		 * @alias keepAlphabetical
		 * @type {boolean}
		 */
		this.keepAlphabetical = getPreference(
			'metadata.keepListAlphabetical',
			true,
		);

		/**
		 * @memberof module:asset/collections/attributes
		 * @alias writableOnly
		 * @type {boolean}
		 */
		this.writableOnly = opts.writableOnly === true;

		// Hey, listen!
		this.listenTo(this, 'sync', (collection, results, opts) => {
			// Models sync too
			if (this !== collection) return;

			this.stopListening(this, 'add change remove');
			this.listenTo(this, 'add', this.attributeAdded);
			this.listenTo(this, 'change', this.attributeChanged);
			this.listenTo(this, 'remove', this.attributeRemoved);
		});
	}

	/**
	 * @method
	 * @param {Netx.Models.Attribute} model Attribute template model
	 * @fires module:asset#asset:attribute:added
	 */
	attributeAdded(model) {
		// Dispatch event
		// TODO: fix this, no dispatching from models/collections
		dispatcher.trigger('asset:attribute:added', model);
	}

	/**
	 * @method
	 * @param {Netx.Models.Attribute} model Attribute template model
	 * @fires module:asset#asset:attribute:changed
	 */
	attributeChanged(model) {
		// Dispatch event
		// TODO: fix this, no dispatching from models/collections
		dispatcher.trigger('asset:attribute:changed', model);
	}

	/**
	 * @method
	 * @param {Netx.Models.Attribute} model Attribute template model
	 * @fires module:asset#asset:attribute:removed
	 */
	attributeRemoved(model) {
		// Dispatch event
		// TODO: fix this, no dispatching from models/collections
		dispatcher.trigger('asset:attribute:removed', model);
	}

	/**
	 * @method
	 * @param {array} ids Array of attribute template ids
	 */
	deleteAttributes(ids) {
		return this.deleteMultiple(ids);
	}

	/**
	 * @method
	 * @param {string} objectType Object type (asset/clip)
	 */
	filterByObjectType(objectType) {
		const filter = () => {
			return this.filter((model) => model.isForObjectType(objectType));
		};

		// When we change, so should the filtered collection
		const collection = new this.constructor(filter());
		return collection
			.listenTo(this, 'add', (attributes, attribute) => {
				collection.add(attribute);
			})
			.listenTo(this, 'remove', (attributes, attribute) => {
				collection.remove(attribute);
			})
			.listenTo(this, 'reset', (attributes) => {
				collection.reset(filter());
			})
			.listenTo(this, 'sync', (attributes, data, opts) => {
				if (attributes !== this) return;
				collection.set(filter(), opts);
				collection.trigger('sync', collection);
			});
	}

	/**
	 * @method
	 * @param {AttributeType[]} types Types of attributes to return
	 * @param {boolean} editableOnly Only return editable attributes
	 * @return {Netx.Collection.Attributes}
	 */
	filterByTypes(types, editableOnly) {
		// Wrap
		if (!_.isArray(types)) {
			types = [types];
		}

		// For now we need to be the holders of this data
		// In 8.12 we will have flags on the templates themselves for this
		const editableSystemAttributes = ['description', 'name'];

		// Return filtered collection
		return new this.constructor(
			this.filter(
				(attr) =>
					// If we want only editable attributes then we need to omit any system attributes that cannot be edited - all other attribute types are editable by default
					_.contains(types, attr.get('type')) &&
					(!editableOnly ||
						!attr.get('system') ||
						_.contains(editableSystemAttributes, attr.get('name'))),
			),
		);
	}

	/**
	 * @method
	 * @return {module:asset/collections/attributes} Attributes tracking history
	 */
	filterByHistory() {
		return new this.constructor(this.where({trackHistory: true}));
	}

	/**
	 * @method
	 * @return {module:asset/collections/assetAttributeDatum} Asset attribute datum
	 */
	getAttributeDatum(defaultValues) {
		const attributeDatum = new AssetAttributeDatumCollection([
			{
				assetId: 0,
				attributes: [],
			},
		]);

		const record = attributeDatum.at(0);

		this.each((model) => {
			let value = defaultValues ? model.get('value') : null;

			if (isEmptyValue(value)) {
				value = [];
			} else if (!_.isArray(value)) {
				value = [value];
			}

			record.addAttribute({
				assetId: 0,
				multivalue: model.get('multivalue'),
				permission: 'w',
				values: value,
				typeName: model.getTypeName(),
				templateId: model.id,
				name: model.get('name'),
			});
		});

		return attributeDatum;
	}

	/**
	 * For attr storage purposes: get an id for a name
	 * Unfortunately in a lot of the old interface, attribute names were used over ids (diplayed attributes, attribute field sets etc)
	 * @method
	 * @param {string} name Attribute name
	 * @return {number} Attribute id
	 */
	getIdByName(name) {
		const attr = this.findWhere({name: name});
		return attr ? attr.get('attributeid') : undefined;
	}

	/**
	 * @method
	 * @param {boolean} withoutDefaultValues Only return attributes without default values
	 * @return {module:asset/collections/attributes}
	 */
	getMandatory(withoutDefaultValues, withoutSystem) {
		return new this.constructor(
			this.filter(
				(m) =>
					m.isMandatory() &&
					(!withoutDefaultValues || !m.hasDefaultValue()) &&
					(!withoutSystem || !m.get('system')),
			),
		);
	}

	/**
	 * @method
	 * @return {Netx.Collections.Attributes} Collection of system attributes
	 */
	getSystemAttributes() {
		return new this.constructor(this.filter((attr) => attr.get('system')));
	}

	/**
	 * Are there mandatory attributes?
	 * @method
	 * @return {array} List of attribute models that are mandatory, or 'undefined' (instead of empty array) if there are none.
	 */
	hasMandatory() {
		const list = this.filter((m) => m.get('mandatory') == 1);
		return list.length ? list : undefined;
	}

	/**
	 * For asset sorting purposes, return an object of names of known attributes.
	 * @method
	 * @param {boolean} system Include system attributes
	 * @return {object} names
	 */
	names(system) {
		const names = {};
		this.each((attr) => {
			if (system && attr.get('system') === true) {
				names[attr.get('code')] = attr.get('name');
			} else if (attr.get('system') !== true) {
				names[attr.get('name')] = attr.get('name');
			}
		});
		return names;
	}

	/**
	 * Vocabulary has been updated
	 * @param {number} vocabularyId Vocabulary ID
	 */
	updatedVocabulary(vocabularyId) {
		// Every attribute that uses this vocab needs to be refetched
		this.each((model) => {
			if (!ATTRIBUTE_VOCABULARY_TYPES.includes(model.get('type'))) return;
			model.get('spec1') === vocabularyId && model.fetch();
		});
	}

	/** @type {string} */
	static get __name__() {
		return 'Attributes';
	}
}
